import { format, parse } from 'date-fns';

/**
 * Formats a date in ISO format (YYYY-MM-DD) for storage in the database
 * This ensures consistent date handling regardless of the user's timezone
 */
export const formatDateForStorage = (date: Date | string | null | undefined): string | null => {
  if (!date) return null;
  
  // If date is already a string in YYYY-MM-DD format, return it
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    // Format as YYYY-MM-DD (ISO format without time component)
    return format(dateObj, 'yyyy-MM-dd');
  } catch (error) {
    console.error('Error formatting date:', error);
    return null;
  }
};

/**
 * Parses a date string from the database into a Date object
 * Handles both ISO format dates and full ISO strings with time
 */
export const parseDateFromStorage = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null;
  
  try {
    // If it's a date-only string (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return parse(dateString, 'yyyy-MM-dd', new Date());
    }
    // Otherwise treat as full ISO string
    return new Date(dateString);
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
};

/**
 * Formats a date for display to the user
 */
export const formatDateForDisplay = (date: Date | string | null | undefined): string => {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return format(dateObj, 'MMMM d, yyyy');
  } catch (error) {
    console.error('Error formatting date for display:', error);
    return '';
  }
};

/**
 * Validates if a date string or Date object is a valid date
 */
export const isValidDate = (date: any): boolean => {
  if (!date) return false;
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return !isNaN(dateObj.getTime());
  } catch (error) {
    return false;
  }
};
